import { fetchWithCache } from './helpers-fetch-cache.js'

if (!customElements.get('rebuy-products-carousel')) {
  const sliderDepsReady = Promise.all([
    customElements.whenDefined('hh-draggable-slider'),
    customElements.whenDefined('hh-product-form'),
  ])

  class RebuyProductsCarousel extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute('loading') && !this._loadingStartedAt) {
        this._loadingStartedAt = Date.now()
      }

      this._loadAbortController?.abort()
      this._loadAbortController = new AbortController()
      this.addEventListener('change', this.onVariantSelectChange.bind(this))
      this.loadProducts()
    }

    disconnectedCallback() {
      this._loadAbortController?.abort()
    }

    onVariantSelectChange(event) {
      const select = event.target

      if (!select.matches('.mini-cart-product-card__variant')) return

      const form = select.form || select.closest('form')
      const button = form?.querySelector('[name="add"]')
      const option = select.options[select.selectedIndex]

      if (button) {
        button.disabled = Boolean(option?.disabled)
      }
    }

    parseCartItems() {
      if (!this.dataset.cartItems) return []

      try {
        const items = JSON.parse(this.dataset.cartItems)
        return Array.isArray(items) ? items : []
      } catch (error) {
        console.error('Failed to parse Rebuy cart items:', error)
        return []
      }
    }

    getCartProductIds() {
      return (this.dataset.cartProductIds || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    }

    buildRequestParams({
      apiKey,
      limit,
      productId,
      cartProductIds,
      cartVariantIds,
      cartItems,
      cartItemCount,
      cartSubtotal,
      countryCode,
      useCustomEndpoint,
    }) {
      const params = new URLSearchParams({
        key: apiKey,
        limit: String(limit),
      })

      const shopifyProductIds = cartProductIds || productId

      if (shopifyProductIds) {
        params.set('shopify_product_ids', shopifyProductIds)
      }

      if (countryCode) {
        params.set('country_code', countryCode)
      }

      if (useCustomEndpoint) {
        if (cartVariantIds) {
          params.set('shopify_variant_ids', cartVariantIds)
        }

        if (cartItemCount) {
          params.set('cart_item_count', cartItemCount)
        }

        if (cartSubtotal) {
          params.set('cart_subtotal', cartSubtotal)
        }

        cartItems.forEach((item, index) => {
          if (!item.product_id) return

          params.append(`cart[items][${index}][product_id]`, String(item.product_id))

          if (item.variant_id) {
            params.append(`cart[items][${index}][variant_id]`, String(item.variant_id))
          }

          params.append(
            `cart[items][${index}][quantity]`,
            String(item.quantity || 1)
          )
        })
      }

      return params
    }

    dedupeProducts(products) {
      const uniqueProducts = []
      const seenHandles = new Set()

      products.forEach((product) => {
        if (!product?.handle || seenHandles.has(product.handle)) return

        seenHandles.add(product.handle)
        uniqueProducts.push(product)
      })

      return uniqueProducts
    }

    filterExcludedProducts(products) {
      const cartProductIds = new Set(this.getCartProductIds())
      const currentProductId = this.dataset.productId
        ? String(this.dataset.productId)
        : ''

      return this.dedupeProducts(products).filter((product) => {
        const productId = product?.id ? String(product.id) : ''

        if (productId && cartProductIds.has(productId)) {
          return false
        }

        if (currentProductId && productId === currentProductId) {
          return false
        }

        return Boolean(product?.handle)
      })
    }

    async loadProducts() {
      const signal = this._loadAbortController.signal
      const apiKey = this.dataset.apiKey?.trim()
      const limit = parseInt(this.dataset.limit, 10) || 8
      const productId = this.dataset.productId
      const cartProductIds = this.dataset.cartProductIds
      const cartVariantIds = this.dataset.cartVariantIds
      const cartItems = this.parseCartItems()
      const cartItemCount = this.dataset.cartItemCount
      const cartSubtotal = this.dataset.cartSubtotal
      const customEndpointId = this.dataset.customEndpointId?.trim()
      const countryCode = this.dataset.countryCode
      const cardSectionId = this.dataset.cardSectionId || 'rebuy-product-card'

      if (!apiKey) {
        console.error('Rebuy recommendations failed: API key is required.')
        this.removeCarousel()
        return
      }

      this.showLoadingState()

      if (signal.aborted) return

      const requestContext = {
        apiKey,
        limit,
        productId,
        cartProductIds,
        cartVariantIds,
        cartItems,
        cartItemCount,
        cartSubtotal,
        countryCode,
      }

      let products = []

      if (customEndpointId) {
        try {
          products = await this.fetchRecommendations({
            ...requestContext,
            customEndpointId,
            useCustomEndpoint: true,
            signal,
          })
        } catch (error) {
          if (signal.aborted) return
          console.warn('Rebuy custom endpoint failed, falling back to recommended:', error)
        }
      }

      if (!products.length) {
        try {
          products = await this.fetchRecommendations({
            ...requestContext,
            customEndpointId: '',
            useCustomEndpoint: false,
            signal,
          })
        } catch (error) {
          if (signal.aborted) return
          console.error('Rebuy recommendations failed:', error)
          this.removeCarousel()
          return
        }
      }

      if (signal.aborted) return

      products = this.filterExcludedProducts(products)

      if (!products.length) {
        this.removeCarousel()
        return
      }

      await this.renderProducts(products, cardSectionId, signal)
    }

    async fetchRecommendations({
      apiKey,
      limit,
      productId,
      cartProductIds,
      cartVariantIds,
      cartItems,
      cartItemCount,
      cartSubtotal,
      customEndpointId,
      countryCode,
      useCustomEndpoint,
      signal,
    }) {
      const params = this.buildRequestParams({
        apiKey,
        limit,
        productId,
        cartProductIds,
        cartVariantIds,
        cartItems,
        cartItemCount,
        cartSubtotal,
        countryCode,
        useCustomEndpoint,
      })

      const url =
        useCustomEndpoint && customEndpointId
          ? `https://rebuyengine.com/api/v1/custom/id/${encodeURIComponent(customEndpointId)}?${params}`
          : `https://rebuyengine.com/api/v1/products/recommended?${params}`

      return fetchWithCache(url, { signal }, async (response) => {
        const data = await response.json()

        if (data.error) {
          throw new Error(data.error)
        }

        return data.data || []
      })
    }

    showLoadingState() {
      const sliderContainer = this.querySelector('[data-rebuy-slider]')

      if (!sliderContainer) {
        return
      }

      if (!sliderContainer.querySelector('.rebuy-carousel__loading')) {
        this._loadingStartedAt = Date.now()

        const loading = document.createElement('div')
        loading.className = 'rebuy-carousel__loading'
        loading.innerHTML = `
          <div class="rebuy-carousel__spinner" role="status" aria-label="Loading recommendations">
            <span class="visually-hidden">Loading recommendations</span>
          </div>
        `

        sliderContainer.appendChild(loading)
      } else if (!this._loadingStartedAt) {
        this._loadingStartedAt = Date.now()
      }

      this.setAttribute('loading', '')
      this.setAttribute('aria-busy', 'true')
      this.hidden = false
      this.removeAttribute('hidden')
    }

    async waitForLoadingPaint() {
      await new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve))
      })

      const minLoadingMs = 500
      const elapsed = Date.now() - (this._loadingStartedAt || 0)
      const remaining = minLoadingMs - elapsed

      if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining))
      }
    }

    createSlider() {
      const slider = document.createElement('hh-draggable-slider')
      slider.className = 'block'
      slider.setAttribute('dots', 'false')

      if (this.dataset.context === 'mini-cart') {
        slider.setAttribute('buttons', 'false')
      } else {
        slider.setAttribute('buttons', 'true')
        slider.setAttribute('icon-type-left', 'chevronLeft')
        slider.setAttribute('icon-type-right', 'chevronRight')
      }

      return slider
    }

    appendCard(slider, product, html) {
      const item = document.createElement('div')
      item.setAttribute('slot', 'item')
      item.dataset.productHandle = product.handle
      item.innerHTML = html
      slider.appendChild(item)
    }

    async renderProducts(products, cardSectionId, signal) {
      const sliderContainer = this.querySelector('[data-rebuy-slider]')

      if (!sliderContainer) {
        this.removeCarousel()
        return
      }

      await sliderDepsReady
      if (signal.aborted) return

      const cardResults = await Promise.all(
        products.map(async (product) => {
          const html = await this.fetchProductCard(product.handle, cardSectionId, signal)

          if (!html || signal.aborted) return null

          return { product, html }
        })
      )

      if (signal.aborted) return

      const validCards = cardResults.filter(Boolean)

      if (!validCards.length) {
        this.removeCarousel()
        return
      }

      await this.waitForLoadingPaint()

      if (signal.aborted) return

      sliderContainer.innerHTML = ''

      const slider = this.createSlider()

      validCards.forEach(({ product, html }) => {
        this.appendCard(slider, product, html)
      })

      this.configureSlider(slider, validCards.length)
      sliderContainer.appendChild(slider)

      await this.waitForSliderReady(slider)
      this.refreshSlider(slider)
      this.removeAttribute('loading')
      this.removeAttribute('aria-busy')
      this.removeAttribute('hidden')
    }

    waitForSliderReady(slider) {
      return new Promise((resolve) => {
        requestAnimationFrame(() => {
          setTimeout(resolve, 0)
        })
      })
    }

    configureSlider(slider, count) {
      if (count < 2) {
        slider.setAttribute('active', 'false')
      } else if (count < 4) {
        slider.setAttribute('active', 'true')
        slider.setAttribute('breakpoint', '750')
      } else {
        slider.setAttribute('active', 'true')
        slider.removeAttribute('breakpoint')
      }
    }

    refreshSlider(slider) {
      const update = () => {
        if (typeof slider.handleResize === 'function') {
          slider.handleResize()
        }

        if (typeof slider.updateUI === 'function') {
          slider.updateUI()
        }
      }

      setTimeout(update, 0)
      requestAnimationFrame(update)

      slider.querySelectorAll('img').forEach((image) => {
        if (!image.complete) {
          image.addEventListener('load', update, { once: true })
        }
      })
    }

    async fetchProductCard(handle, sectionId, signal) {
      if (!handle) return null

      try {
        return await fetchWithCache(
          `/products/${encodeURIComponent(handle)}?section_id=${sectionId}`,
          { signal },
          async (response) => {
            const text = await response.text()
            const doc = new DOMParser().parseFromString(text, 'text/html')
            const card = doc.querySelector('[data-rebuy-product-card]')
            return card ? card.innerHTML : null
          }
        )
      } catch (error) {
        if (signal?.aborted) return null
        console.error(`Failed to fetch Rebuy product card for ${handle}:`, error)
        return null
      }
    }

    removeCarousel() {
      this.removeAttribute('loading')
      this.removeAttribute('aria-busy')

      const miniCartRebuy = this.closest('.mini-cart__rebuy')
      if (miniCartRebuy) {
        miniCartRebuy.remove()
        return
      }

      this.remove()
    }
  }

  customElements.define('rebuy-products-carousel', RebuyProductsCarousel)
}
